import React, { useEffect, useState } from "react";
import { SuperAdminLayout } from "../components/SuperAdminLayout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { FormField } from "../components/ui/form-field";
import { toast } from "sonner";
import {
  Sparkles, Bell, CheckCircle2, Zap, Star, Plus, Trash2, Send, Loader2,
} from "lucide-react";

const ICONS = [
  { key: "Sparkles", Comp: Sparkles, label: "Brilho" },
  { key: "Bell", Comp: Bell, label: "Sino" },
  { key: "CheckCircle2", Comp: CheckCircle2, label: "Concluído" },
  { key: "Zap", Comp: Zap, label: "Raio" },
  { key: "Star", Comp: Star, label: "Estrela" },
];

const AUDIENCES = [
  { key: "all", label: "Todos os usuários" },
  { key: "admin", label: "Apenas admins/supervisores" },
  { key: "collaborator", label: "Apenas colaboradores" },
];

export default function SuperAdminReleaseNotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    version: "",
    title: "",
    body: "",
    audience: "all",
    icon: "Sparkles",
  });

  async function load() {
    setLoading(true);
    try {
      // Re-use the unseen endpoint just to show *something*; for the SA we
      // also want to see history. Filter inactive on the client.
      const { data } = await api.get("/release-notes/unseen");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function publish() {
    if (!form.version || !form.title || !form.body) {
      toast.error("Preencha versão, título e conteúdo");
      return;
    }
    setSaving(true);
    try {
      await api.post("/release-notes", form);
      toast.success("Aviso publicado para todos os usuários");
      setForm({ version: "", title: "", body: "", audience: "all", icon: "Sparkles" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao publicar");
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(id) {
    if (!window.confirm("Desativar este aviso? Não aparecerá mais para novos usuários.")) return;
    try {
      await api.delete(`/release-notes/${id}`);
      toast.success("Aviso desativado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <SuperAdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <Sparkles size={26} /> Avisos de Novidades
        </h1>
        <p className="text-sm text-slate-300 mt-1 max-w-2xl">
          Cada vez que houver uma alteração relevante no sistema, publique um
          aviso aqui. Ele aparece automaticamente como modal para o usuário
          (admin ou colaborador) na primeira vez que ele abrir o app, e some
          após ele clicar em "Entendi".
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Composer */}
        <div className="cm-card bg-white/5 border-white/10 p-5 space-y-3" data-testid="release-notes-composer">
          <div className="text-sm font-bold text-white flex items-center gap-2">
            <Plus size={16} /> Novo aviso
          </div>

          <div className="grid grid-cols-3 gap-2">
            <FormField label="Versão">
              <input
                data-testid="release-version"
                className="cm-input"
                placeholder="2.4.0"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
              />
            </FormField>
            <FormField label="Para quem">
              <select
                data-testid="release-audience"
                className="cm-input"
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
              >
                {AUDIENCES.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Ícone">
              <select
                data-testid="release-icon"
                className="cm-input"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
              >
                {ICONS.map((i) => (
                  <option key={i.key} value={i.key}>{i.label}</option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label="Título">
            <input
              data-testid="release-title"
              className="cm-input"
              placeholder="Botão Cobrar agora em /recebimentos"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </FormField>

          <FormField label="O que mudou">
            <textarea
              data-testid="release-body"
              className="cm-input min-h-[140px]"
              rows={6}
              placeholder="Conte de forma clara o que o usuário ganha…&#10;&#10;• Use bullets pra listar funcionalidades&#10;• Quebras de linha são preservadas"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </FormField>

          <Button
            data-testid="release-publish"
            onClick={publish}
            disabled={saving}
            className="bg-violet-600 hover:bg-violet-700 w-full"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
            Publicar aviso para todos
          </Button>
        </div>

        {/* History */}
        <div className="space-y-3">
          <div className="text-sm font-bold text-white">
            Avisos ativos {loading ? "" : `(${items.length})`}
          </div>
          {loading ? (
            <div className="cm-card bg-white/5 border-white/10 p-5 text-slate-300">
              Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="cm-card bg-white/5 border-white/10 p-5 text-slate-300 text-sm">
              Nenhum aviso ativo. Publique o primeiro à esquerda.
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                data-testid={`release-note-${n.id}`}
                className="cm-card bg-white/5 border-white/10 p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-blue-300 font-semibold">
                    v{n.version} · {AUDIENCES.find((a) => a.key === n.audience)?.label}
                  </div>
                  <button
                    type="button"
                    onClick={() => deactivate(n.id)}
                    data-testid={`release-deactivate-${n.id}`}
                    className="p-1.5 rounded-md text-rose-300 hover:bg-rose-500/20"
                    title="Desativar"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="text-white font-semibold mt-1">{n.title}</div>
                <div className="text-sm text-slate-300 mt-1 whitespace-pre-line">{n.body}</div>
                <div className="text-[11px] text-slate-400 mt-2">
                  Publicado em {new Date(n.published_at).toLocaleString("en-US")}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
